import { PrincipalType } from '@tunasong/schemas'
import type { ACL, ItemOperation } from '@tunasong/schemas'
import type { Owned } from './owned.js'

export const permImplies = (permA: ItemOperation, permB: ItemOperation) => {
  if (permA === 'WRITE' && permB === 'READ') {
    return true
  }
  return permA === permB
}

/**
 * ACL principal namespace for an authorized link. The `<id>` is the opaque `linkId` from
 * the access-link record — never the token, which would put a live credential in an
 * array every reader of the entity can see.
 */
export const linkPrincipalPrefix = 'link:'

export const linkPrincipal = (linkId: string) => `${linkPrincipalPrefix}${linkId}`

/**
 * The effective permission an ACL confers. `PUBLIC` and `link:` are the two principals
 * a caller can match without authenticating as a user, so neither may ever confer a
 * mutating permission: such a grant is downgraded to READ rather than ignored, which
 * removes the escalation without un-sharing an entity its owner meant to be readable.
 *
 * This is enforced here (not in `aclSchema`) so that ACLs already persisted
 * with an illegal combination are neutralised on read. `grantableAclSchema`
 * stops new ones being stored.
 */
const effectivePermission = (acl: ACL): ItemOperation =>
  (acl.principal === PrincipalType.PUBLIC || acl.principal.startsWith(linkPrincipalPrefix)) &&
  acl.permission !== 'READ'
    ? 'READ'
    : acl.permission

/**
 * A grant with an `expiresAt` in the past does not match at all — an expired grant
 * behaves as no grant. An `expiresAt` that cannot be parsed is treated as expired, so a
 * malformed value fails closed rather than granting forever.
 */
const isLive = (acl: ACL, now: number) => {
  if (acl.expiresAt === undefined) {
    return true
  }
  const expiresAt = Date.parse(acl.expiresAt)
  return !Number.isNaN(expiresAt) && expiresAt > now
}

/** When not authenticated, use null as the principal */
export const hasACLPermission = (
  principal: string | null,
  acls?: ACL[],
  perm: ItemOperation = 'READ',
  now: number = Date.now()
) =>
  Boolean(principal || principal === null) &&
  Array.isArray(acls) &&
  Boolean(
    acls.find(
      acl =>
        isLive(acl, now) &&
        (acl.principal === PrincipalType.PUBLIC ||
          // `AUTHENTICATED` means "any logged-in user" — an anonymous caller (null principal) must
          // not satisfy it; only PUBLIC is anonymous-accessible. Fail-closed (W5, docs/SECURITY.md).
          (principal !== null && acl.principal === PrincipalType.AUTHENTICATED) ||
          (principal !== null && acl.principal === principal)) &&
        permImplies(effectivePermission(acl), perm)
    )
  )

export const hasPublicAccess = (acls?: ACL[], now: number = Date.now()) =>
  Boolean(acls?.find(acl => acl.principal === 'PUBLIC' && acl.permission === 'READ' && isLive(acl, now)))

export const canRead = (principal: string | null, acls?: ACL[], now: number = Date.now()) =>
  Boolean(
    hasACLPermission(principal, acls, 'READ', now) ||
      hasACLPermission(principal, acls, 'WRITE', now) ||
      hasPublicAccess(acls, now)
  )

export const canReadEntity = <T>(userId: string | undefined | null, entity?: Owned<T>) =>
  Boolean(entity && userId && (userId === entity.userId || canRead(userId, entity.acls)))

export const canWriteEntity = <T>(userId: string | undefined | null | undefined, entity?: Owned<T>) =>
  Boolean(entity && userId && (userId === entity.userId || canWrite(userId, entity.acls)))

export const canWrite = (principal: string | null, acls?: ACL[], now: number = Date.now()) =>
  hasACLPermission(principal, acls, 'WRITE', now)

export const canDelete = (principal: string | null, acls?: ACL[], now: number = Date.now()) =>
  hasACLPermission(principal, acls, 'DELETE', now)

/** We remove duplicates server-side, so we can simply append them on the client */
export const mergeACLs = (acls?: ACL[] | null, newAcls?: ACL[] | null) => [...(acls ?? []), ...(newAcls ?? [])]
